import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("tw1_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function clearSessionAndRedirect(reason) {
  localStorage.removeItem("tw1_token");
  localStorage.removeItem("tw1_user");
  if (reason) localStorage.setItem("tw1_login_reason", reason);
  window.location.href = "/";
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // v24 R5: alleen een GET bij 502/503/504 of netwerkfout max 2× opnieuw (na ~0,5 s en ~1,5 s); nooit POST/PUT/PATCH/DELETE.
    const cfg = err.config;
    if (cfg && (cfg.method || "get").toLowerCase() === "get" && err.code !== "ERR_CANCELED"
        && (!err.response || [502, 503, 504].includes(err.response.status)) && (cfg.__poging || 0) < 2) {
      cfg.__poging = (cfg.__poging || 0) + 1;
      const jitter = Math.random() * 300;   // v24-R1 F5: tabs niet in de maat
      return new Promise((r) => setTimeout(r, (cfg.__poging === 1 ? 500 : 1500) + jitter)).then(() => api(cfg));
    }
    const url = err.config?.url || "";
    const isLoginCall = url.includes("/auth/login");
    // Achtergrond-polls en foto-uploads (die tonen zelf "Upload mislukt" + "Opnieuw") mogen geen toast-storm veroorzaken.
    const isBackgroundPoll = url.includes("/nav-counts") || url.includes("/weekritme-requests") || /\/bezoeken\/[^/]+\/fotos/.test(url);
    const hasToken = !!localStorage.getItem("tw1_token");

    // Geen response = netwerk-/CORS-fout (bv. een verouderde link/bookmark naar een oud adres).
    if (!err.response) {
      if (!isLoginCall && !isBackgroundPoll) {
        const backend = process.env.REACT_APP_BACKEND_URL || "de server";
        let here = "";
        try { here = window.location.origin; } catch { /* ignore */ }
        const hier = here && here !== backend ? ` Je gebruikt nu ${here} — het juiste adres is ${backend}.` : "";
        // Vaste id: vervangt i.p.v. stapelt, ongeacht hoeveel requests falen.
        toast.error(`De app kan de server niet bereiken. Mogelijk gebruik je een verouderde link of bookmark.${hier}`, { id: "network-error" });
      }
      return Promise.reject(err);
    }

    const status = err.response.status;
    const detail = (err.response.data?.detail || "").toString();
    const code = (err.response.data?.code || "").toString();

    if (status === 403 && code === "MUST_CHANGE_PASSWORD" && hasToken && !isLoginCall) {
      // Vangnet: een verzoek werd geweigerd omdat het wachtwoord nog gewijzigd moet worden.
      // Zet de vlag lokaal en stuur naar het geforceerde wachtwoordscherm — niet uitloggen.
      try {
        const u = JSON.parse(localStorage.getItem("tw1_user") || "null");
        if (u && !u.mustChangePassword) {
          u.mustChangePassword = true;
          localStorage.setItem("tw1_user", JSON.stringify(u));
          window.location.href = "/";
        }
      } catch { /* ignore */ }
    } else if (status === 401 && hasToken && !isLoginCall) {
      clearSessionAndRedirect("Je sessie is verlopen — log opnieuw in.");
    } else if (status === 403 && /gedeactiveerd|deactiv/i.test(detail) && hasToken && !isLoginCall) {
      // Account is tijdens de sessie gedeactiveerd → uitloggen met reden i.p.v. lege lijsten tonen.
      clearSessionAndRedirect(detail || "Je account is gedeactiveerd.");
    }
    return Promise.reject(err);
  }
);

// v24-R1 F4: een fout die opnieuw proberen niet oplost (de server weigert); alleen 5xx/netwerk/timeout wordt herhaald.
export const isPermanenteFout = (e) => [400, 403, 404, 409, 422].includes(e?.response?.status);
